import { v4 as uuid } from 'uuid'

import type {
  ImportItem,
  ImportItemMedia,
  ImportItemPost,
} from '../MigrationWizard/types'

/**
 * Headers expected in a Hootsuite CSV export (after PapaParse header transform:
 * lowercase, spaces→underscores, quotes stripped).
 */
const HOOTSUITE_FINGERPRINT_HEADERS = [
  'social_account',
  'account_name',
  'scheduled_time',
] as const

/**
 * Detect whether a set of CSV headers match the Hootsuite export format.
 */
export function isHootsuiteFormat(headers: string[]): boolean {
  return HOOTSUITE_FINGERPRINT_HEADERS.every((h) => headers.includes(h))
}

type HootsuiteCsvRow = Record<string, string>

/**
 * Parse a Hootsuite CSV row into a normalized ImportItem.
 * Timezone conversion is deferred — scheduledTime is parsed as-is from the CSV
 * and the timezone offset is applied during import execution.
 */
export function parseHootsuiteRow(
  row: HootsuiteCsvRow,
  rowIndex: number,
): ImportItem {
  const errors: string[] = []

  const text = row.content?.trim() ?? ''
  const media = parseMedia(row.attached_media, row.media_links, row.alt_text)
  const tags = row.tags
    ? row.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : undefined
  const linkToPublishedPost = row.link_to_published_post?.trim() || undefined

  const scheduledTime = parseHootsuiteTime(row.scheduled_time)
  if (row.scheduled_time && !scheduledTime) {
    errors.push(
      `Could not parse scheduled time: "${row.scheduled_time}". Expected MM/DD/YYYY HH:MM:SS.`,
    )
  }

  const originalStatus = parsePostStatus(row.post_status)

  if (!text && !media) {
    errors.push('Row has no text content and no media.')
  }

  const sourcePlatform = row.social_account?.trim() ?? ''
  const sourceAccountName = row.account_name?.trim() ?? ''

  if (!sourcePlatform) {
    errors.push('Missing social account (platform).')
  }

  const post: ImportItemPost = {
    text,
    scheduledTime: scheduledTime ?? undefined,
    originalStatus,
    media,
    tags,
    linkToPublishedPost,
  }

  return {
    id: uuid(),
    sourceRow: rowIndex + 2, // +2 for 1-indexed + header row
    status: errors.length > 0 ? 'error' : 'valid',
    errors,
    channel: {
      sourcePlatform,
      sourceAccountName,
    },
    post,
  }
}

/**
 * Extract unique source channels from parsed items.
 * Returns a map of "platform|accountName" → { sourcePlatform, sourceAccountName, postCount }.
 */
export function extractSourceChannels(
  items: ImportItem[],
): Map<
  string,
  { sourcePlatform: string; sourceAccountName: string; postCount: number }
> {
  const channels = new Map<
    string,
    { sourcePlatform: string; sourceAccountName: string; postCount: number }
  >()

  for (const item of items) {
    const key = `${item.channel.sourcePlatform}|${item.channel.sourceAccountName}`
    const existing = channels.get(key)
    if (existing) {
      existing.postCount++
    } else {
      channels.set(key, {
        sourcePlatform: item.channel.sourcePlatform,
        sourceAccountName: item.channel.sourceAccountName,
        postCount: 1,
      })
    }
  }

  return channels
}

/**
 * Parse Hootsuite's time format: MM/DD/YYYY HH:MM:SS
 * Returns a Date object (interpreted as-is — timezone handling happens later).
 */
function parseHootsuiteTime(timeStr: string | undefined): Date | null {
  if (!timeStr?.trim()) return null

  const match = timeStr
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/)
  if (!match) return null

  const [, month, day, year, hours, minutes, seconds] = match
  const date = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    parseInt(hours, 10),
    parseInt(minutes, 10),
    parseInt(seconds, 10),
  )

  if (isNaN(date.getTime())) return null
  return date
}

/**
 * Parse media fields from a Hootsuite CSV row.
 * media_links can contain multiple URLs (newline or comma separated) —
 * we take only the first one for v1.
 */
function parseMedia(
  mediaTypeStr: string | undefined,
  mediaLinksStr: string | undefined,
  altTextStr: string | undefined,
): ImportItemMedia | undefined {
  if (!mediaTypeStr) return undefined
  const lower = mediaTypeStr.trim().toLowerCase()
  if (lower !== 'image' && lower !== 'video') return undefined

  const url = mediaLinksStr
    ?.split(/[\n,]/)
    .map((u) => u.trim())
    .find((u) => u.length > 0)

  if (!url) return undefined

  return {
    type: lower,
    url,
    altText: altTextStr?.trim() || undefined,
  }
}

function parsePostStatus(
  statusStr: string | undefined,
): ImportItemPost['originalStatus'] {
  if (!statusStr) return 'unknown'
  const lower = statusStr.trim().toLowerCase()
  if (lower === 'draft') return 'draft'
  if (lower === 'scheduled') return 'scheduled'
  if (lower === 'sent' || lower === 'published') return 'sent'
  return 'unknown'
}
